import React, { useState } from 'react';
import {
	cutLyricsOf,
	cutLyricsLabel,
	driveLinkOf,
	embedUrlFor,
	groupStaffFilesByLength,
	isPlayable,
	isSheet,
	staffTrackLabel,
	trackKey,
} from '../../services/studentTracksModel';
import './TrackPills.css';
import KeyControl from './KeyControl';
import SaveKeyButton from './SaveKeyButton';
import RoleButtons from './RoleButtons';

/**
 * The teacher's track strip: one pill per library file for one song, wired to
 * the page's single open slot (useTrackPlayer). Tap a pill and it plays in
 * the room: a file with a Storage copy plays in our own <audio> — the pill
 * flips to pause and a transport row appears beneath (pause · elapsed over
 * length · seek · Open in Drive); a file not yet copied opens Drive's own
 * embedded player in the same slot instead. Either way ONE thing is open on
 * the page. Files the strip does not play — Sheet PDF, the Score source —
 * are links that open in a new tab, drawn in the same row so the song's
 * whole folder reads in one place.
 *
 * Every pill is always rendered and always coloured; nothing here is hover-
 * gated (keratoconus; the PWA has no hover).
 *
 * Renders NOTHING for a song with no files rather than an empty box: a track
 * pointer is optional on most songs, and an empty strip on every tab would be
 * chrome with no information.
 */
const TrackPills = ({ files = [], player, compact = false, onSaveKey = null, saveKeyWho = null, roles = null }) => {
	const rows = groupStaffFilesByLength(files);
	// Which Length row's words are open — local: the words are a reading aid
	// for THIS strip, not a page-wide state like the player.
	const [openLyrics, setOpenLyrics] = useState(null);
	if (rows.length === 0 || !player) return null;
	const current = player.current;
	const currentHere = current && files.some((f) => trackKey(f) === current.key);
	const embedUrl = currentHere ? embedUrlFor(current.file) : null;

	return (
		<div className={`tp${compact ? ' tp--compact' : ''}`} data-testid="track-pills">
			{rows.map((row) => {
				const lyrics = cutLyricsOf(row.files);
				const lyricsOpen = Boolean(lyrics) && openLyrics === row.length;
				return (
				<React.Fragment key={row.length}>
				<div className="tp__row">
					{rows.length > 1 && <span className="tp__length">{row.length}</span>}
					{row.files.map((f) => {
						const key = trackKey(f);
						if (!isPlayable(f)) {
							return (
								<a
									key={key}
									className="tp__pill tp__pill--link"
									href={f.driveUrl}
									target="_blank"
									rel="noopener noreferrer"
									title={f.name || staffTrackLabel(f)}>
									<i
										className={`fa-sharp fa-solid ${isSheet(f) ? 'fa-file-pdf' : 'fa-file-music'}`}
										aria-hidden="true"
									/>
									{staffTrackLabel(f)}
								</a>
							);
						}
						const isCurrent = player.isCurrent(f);
						const playing = isCurrent && current.engine === 'audio' && player.isPlaying;
						const icon = playing ? 'fa-pause' : isCurrent && current.engine === 'embed' ? 'fa-waveform-lines' : 'fa-play';
						return (
							<button
								key={key}
								type="button"
								className={`tp__pill${isCurrent ? ' is-current' : ''}${playing ? ' is-playing' : ''}`}
								aria-pressed={isCurrent}
								title={f.name || staffTrackLabel(f)}
								onClick={() => player.toggle(f)}>
								<i className={`fa-sharp fa-solid ${icon}`} aria-hidden="true" />
								{staffTrackLabel(f)}
								{/* Recital / Audition (step 6): always shown on the pill, so
								    the chosen cut is findable without playing anything. */}
								{roles && roles.roleOf(f) && (
									<span className={`tp__role tp__role--${roles.roleOf(f)}`}>
										{roles.roleOf(f) === 'recital' ? 'Recital' : 'Audition'}
									</span>
								)}
							</button>
						);
					})}
					{/* The cut's words (cutLyrics.js). A toggle, not always-open text:
					    the strip sits above the notes on a phone, and thirteen lines
					    on every tab would push the jot list off the screen. The pill
					    itself is always rendered, so the words are one tap away. */}
					{lyrics && (
						<button
							type="button"
							className={`tp__pill tp__pill--lyrics${lyricsOpen ? ' is-current' : ''}`}
							aria-pressed={lyricsOpen}
							aria-label={`${lyricsOpen ? 'Hide' : 'Show'} the words of this cut`}
							onClick={() => setOpenLyrics(lyricsOpen ? null : row.length)}>
							<i className="fa-sharp fa-solid fa-align-left" aria-hidden="true" />
							{/* "Cut words" (Lesson Dashboard pass 3 F5): "Lyrics" is also a
							    Song panel mode, and these are one cut's timed words. */}
							Cut words
						</button>
					)}
				</div>
				{lyricsOpen && (
					<div className={`tp__lyrics${lyrics.unverified ? ' tp__lyrics--unverified' : ''}`} data-testid="track-lyrics">
						<p className="tp__lyricslabel">
							<i className={`fa-sharp fa-solid ${lyrics.unverified ? 'fa-triangle-exclamation' : 'fa-circle-check'}`} aria-hidden="true" />
							{cutLyricsLabel(lyrics)}
						</p>
						{lyrics.lines.map((line, i) => (
							<p className="tp__lyricline" key={i}>
								{line}
							</p>
						))}
					</div>
				)}
				</React.Fragment>
				);
			})}

			{currentHere && current.engine === 'audio' && (
				<div className="tp__transport tp__transport--audio" data-testid="track-transport">
					<button
						type="button"
						className="tp__tbtn"
						aria-label={player.isPlaying ? 'Pause' : 'Play'}
						onClick={() => player.toggle(current.file)}>
						<i className={`fa-sharp fa-solid ${player.isPlaying ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
					</button>
					<span className="tp__time">
						{player.formatTime(player.currentTime)}
						<span className="tp__timesep"> / </span>
						{player.formatTime(player.duration)}
					</span>
					<input
						className="tp__seek"
						type="range"
						min="0"
						max={player.duration > 0 ? player.duration : 0}
						step="0.1"
						value={player.duration > 0 ? player.currentTime : 0}
						aria-label="Seek"
						onChange={(e) => player.seek(parseFloat(e.target.value))}
					/>
					{/* A portal-made track (Add a track) has no Drive copy yet; the
					    link renders only when there is somewhere for it to go. */}
					{driveLinkOf(current.file) && (
						<a
							className="tp__open"
							href={driveLinkOf(current.file)}
							target="_blank"
							rel="noopener noreferrer"
							title="Open in Drive">
							<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
							<span className="tp__opentext">Drive</span>
						</a>
					)}
					<KeyControl player={player} />
					<SaveKeyButton files={files} player={player} onSaveKey={onSaveKey} who={saveKeyWho} />
					{roles && <RoleButtons file={current.file} roles={roles} />}
				</div>
			)}
			{currentHere && current.engine === 'audio' && player.error && (
				<p className="tp__error" role="alert">
					{player.error}
				</p>
			)}

			{currentHere && current.engine === 'embed' && (
				<div className="tp__transport" data-testid="track-transport">
					{embedUrl ? (
						<iframe
							className="tp__embed"
							src={embedUrl}
							title={`Play ${staffTrackLabel(current.file)}`}
							allow="autoplay"
							allowFullScreen={false}
						/>
					) : (
						<p className="tp__error" role="alert">
							This track has no Drive link yet.
						</p>
					)}
					<div className="tp__transportfoot">
						<span className="tp__now">{current.file.name || staffTrackLabel(current.file)}</span>
						<a
							className="tp__open"
							href={current.file.driveUrl}
							target="_blank"
							rel="noopener noreferrer"
							title="Open in Drive">
							<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
							<span className="tp__opentext">Open in Drive</span>
						</a>
					</div>
				</div>
			)}
		</div>
	);
};

export default TrackPills;
